import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getArticlesByCategory, getHotArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = slug ? getArticlesByCategory(slug) : [];
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : getArticlesByCategory(categories[0]?.slug || slug || 'all');
  const hotArticles = getHotArticles().slice(0, 4);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroBgImage = category.slug === 'yulequan'
    ? 'https://readdy.ai/api/search-image?query=Glamorous%20red%20carpet%20event%20with%20dramatic%20lighting%2C%20paparazzi%20flashes%2C%20luxurious%20atmosphere%2C%20dark%20background%20with%20warm%20golden%20accents%2C%20cinematic%20magazine%20cover%20style%2C%20rich%20contrast%2C%20elegant%20composition&width=1600&height=900&seq=cat-hero-yulequan&orientation=landscape'
    : category.slug === 'zhubo'
      ? 'https://readdy.ai/api/search-image?query=Professional%20live%20streaming%20studio%20setup%20with%20dramatic%20lighting%2C%20multiple%20screens%20and%20cameras%2C%20dark%20background%20with%20neon%20accents%2C%20modern%20tech%20atmosphere%2C%20editorial%20photography%20style%2C%20rich%20contrast%2C%20clean%20composition&width=1600&height=900&seq=cat-hero-zhubo&orientation=landscape'
      : category.slug === 'wanghong'
        ? 'https://readdy.ai/api/search-image?query=Social%20media%20content%20creation%20workspace%20with%20smartphone%20and%20ring%20light%2C%20trendy%20accessories%2C%20warm%20ambient%20lighting%2C%20modern%20minimal%20setup%2C%20dark%20background%2C%20editorial%20style%2C%20clean%20composition&width=1600&height=900&seq=cat-hero-wanghong&orientation=landscape'
        : 'https://readdy.ai/api/search-image?query=Breaking%20news%20concept%20with%20abstract%20dynamic%20shapes%2C%20dramatic%20lighting%2C%20warm%20orange%20and%20red%20tones%2C%20powerful%20visual%20impact%2C%20editorial%20concept%20photography%2C%20dark%20background%2C%20clean%20composition&width=1600&height=900&seq=cat-hero-shehui&orientation=landscape';

  const topThree = categoryArticles.slice(0, 3);
  const padded = [...topThree];
  while (padded.length < 3) {
    padded.push(categoryArticles[0] || topThree[0]);
  }

  return (
    <>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "name": `${category.name} - 黑料吃瓜在线`,
            "url": `http://yusuwangluo.cn/category/${category.slug}`,
            "description": category.description,
            "isPartOf": { "@type": "WebSite", "name": "黑料吃瓜在线", "url": "http://yusuwangluo.cn/" }
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://yusuwangluo.cn/" },
              { "@type": "ListItem", "position": 2, "name": category.name }
            ]
          }
        ]
      }} />
      <Navbar />
      <main>
        <section className="relative w-full min-h-[500px] md:min-h-[580px] flex flex-col items-center justify-start pt-24 md:pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroBgImage} alt={category.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight">
              {category.name}
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed">
              {category.description}
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-foreground-950 rounded-full text-sm font-bold hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              返回首页
              <i className="ri-arrow-right-line"></i>
            </Link>

            {padded.length > 0 && (
              <div className="mt-14 md:mt-16 relative flex items-end justify-center gap-0 -mb-36 md:-mb-44">
                {padded.map((article, index) => (
                  <Link
                    key={`${article.id}-${index}`}
                    to={`/article/${article.id}`}
                    className={`relative w-40 md:w-52 flex-shrink-0 cursor-pointer transition-transform duration-300 hover:scale-105 ${
                      index === 0
                        ? 'z-10 -rotate-6 translate-x-10 md:translate-x-14 translate-y-5'
                        : index === 1
                          ? 'z-20 -translate-y-3 scale-110'
                          : 'z-10 rotate-6 -translate-x-10 md:-translate-x-14 translate-y-5'
                    }`}
                  >
                    <div className="bg-background-50 rounded-xl overflow-hidden shadow-lg">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="p-3">
                        <h4 className="text-xs font-semibold text-foreground-950 line-clamp-1">{article.title}</h4>
                        <p className="text-xs text-secondary-500 mt-1">{article.date}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-18 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: category.name }]} />

            <div className="flex flex-col lg:flex-row gap-8 mt-4">
              <div className="flex-1">
                <div className="mb-8">
                  <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-2">
                    {category.name}
                  </h2>
                  <p className="text-sm text-secondary-500">
                    共 {categoryArticles.length} 篇爆料文章
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {categoryArticles.length === 0 && (
                  <div className="text-center py-20">
                    <p className="text-secondary-500">暂无该分类的文章</p>
                    <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-2 inline-block">返回首页</Link>
                  </div>
                )}
              </div>

              <aside className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-fire-line text-accent-500"></i>
                      热门爆料
                    </h3>
                    <div className="flex flex-col gap-2">
                      {hotArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-background-200/50 transition-colors group"
                        >
                          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                            <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                              {article.title}
                            </h4>
                            <p className="text-xs text-secondary-500 mt-1">{article.date}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}